import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { liveTournamentsQuery } from '@/api/queries';
import { translateSportsLabel } from '@/i18n/sports-label';
import { cn } from '@/lib/cn';
import { getTournamentOrCategoryBadge, isFlagBadgeSrc } from '@/shell/sports-menu-utils';
import { HorizontalScrollRow } from '@/ui';

export interface LiveTournamentFilterProps {
  sportName: string;
  /** Selected tournament Ids — empty means "All" (no client filter). */
  selectedTournamentIds: number[];
  onChange: (tournamentIds: number[]) => void;
  className?: string;
}

/**
 * Compact live tournament chips (Figma TournamentFilterCompact). Multi-selects
 * tournaments for client-side filtering; "All" clears the selection.
 */
export function LiveTournamentFilter({
  sportName,
  selectedTournamentIds,
  onChange,
  className,
}: LiveTournamentFilterProps) {
  const { t, i18n } = useTranslation();
  const filters = useMemo(
    () => ({
      Sport: sportName,
      Categories: [] as string[],
      Tournaments: [] as string[],
      EventIds: [] as number[],
      Take: 0,
    }),
    [sportName],
  );
  const { data: tournaments, isPending, isError } = useQuery(liveTournamentsQuery(filters));

  const sortedTournaments = useMemo(
    () =>
      tournaments
        ? [...tournaments].filter((item) => !item.Blocked).sort((a, b) => a.Position - b.Position)
        : [],
    [tournaments],
  );

  const selectedIdSet = useMemo(() => new Set(selectedTournamentIds), [selectedTournamentIds]);

  if (isPending) {
    return (
      <div className={cn(className)} aria-busy="true">
        <HorizontalScrollRow
          aria-label={t('Tournaments', { defaultValue: 'Tournaments' })}
          withDesktopArrows
          className="h-7 items-center gap-1.5"
        >
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-7 min-w-24 shrink-0 animate-pulse rounded-md bg-bg-elevated" />
          ))}
        </HorizontalScrollRow>
      </div>
    );
  }

  if (isError || sortedTournaments.length === 0) {
    return null;
  }

  const allLabel = t('All', { defaultValue: 'All' });
  const allSelected = selectedTournamentIds.length === 0;

  return (
    <nav
      aria-label={t('Live tournaments', { defaultValue: 'Live tournaments' })}
      className={cn('min-w-0 overflow-clip', className)}
    >
      <HorizontalScrollRow
        aria-label={t('Tournaments', { defaultValue: 'Tournaments' })}
        withDesktopArrows
        className="h-7 items-center gap-1.5"
      >
        <button
          type="button"
          role="listitem"
          onClick={() => onChange([])}
          className={cn(
            'relative flex h-7 shrink-0 items-center rounded-md px-2 text-[11px] leading-[14px] whitespace-nowrap',
            allSelected
              ? 'bg-action-primary-hover text-text-on-action'
              : 'bg-bg-elevated text-text-secondary',
          )}
        >
          {allLabel}
        </button>

        {sortedTournaments.map((tournament) => {
          const selected = selectedIdSet.has(tournament.Id);
          const label = translateSportsLabel(i18n, t, tournament.Id, tournament.Name);
          const crestSrc = getTournamentOrCategoryBadge(tournament.Name, tournament.Category);

          return (
            <button
              key={tournament.Id}
              type="button"
              role="listitem"
              aria-pressed={selected}
              onClick={() => {
                if (selected) {
                  onChange(selectedTournamentIds.filter((id) => id !== tournament.Id));
                } else {
                  onChange([...selectedTournamentIds, tournament.Id]);
                }
              }}
              className={cn(
                'relative flex h-7 shrink-0 items-center gap-1.5 rounded-md py-1 pr-2 pl-1.5',
                selected
                  ? 'bg-action-primary-hover text-text-on-action'
                  : 'bg-bg-elevated text-text-secondary',
              )}
            >
              <span
                className={cn(
                  'flex size-[18px] shrink-0 items-center justify-center overflow-clip rounded-full',
                  crestSrc && !isFlagBadgeSrc(crestSrc) && 'bg-white',
                )}
              >
                {crestSrc ? (
                  <img
                    src={crestSrc}
                    alt=""
                    className={
                      isFlagBadgeSrc(crestSrc)
                        ? 'size-full object-cover'
                        : 'size-full object-contain p-0.5'
                    }
                  />
                ) : (
                  <span className="text-[8px] font-bold leading-none text-gray-500">
                    {label.slice(0, 2).toUpperCase()}
                  </span>
                )}
              </span>
              <span className="text-[11px] leading-[14px] whitespace-nowrap">{label}</span>
            </button>
          );
        })}
      </HorizontalScrollRow>
    </nav>
  );
}
